import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import WorkOrder from 'shared/interfaces/WorkOrder.interface';
import { getWorkOrdersWithFilter } from 'shared/api/workOrders';
import useDebounce from 'shared/hooks/useDebounce';
import { setAlert } from 'shared/store/features/alertSlice';
import { toggleTableLoaderVisibility } from 'shared/store/features/tableLoaderSlice';
import { RootState } from 'shared/store/rootReducer';
import { setWorkOrders } from 'shared/store/features/workOrdersSlice';
import { setSelectedWorkOrders } from 'shared/store/features/workOrdersTableSlice';
import { CampaignStatus } from './filterOptions.enum';
import { cookieBanner } from '../../shared/components/Alert';

const useBillingDashboardLogic = () => {
  const { isArchiveFilterModalOpened } = useSelector(
    (state: RootState) => state.archiveFilterModal
  );
  const { archiveFilterStatus } = useSelector(
    (state: RootState) => state.archiveFilterStatus
  );
  const dispatch = useDispatch();
  const [campaignStatusFilter, setCampaignStatusFilter] = useState<CampaignStatus[]>([]);
  const [isCampaignStatusFilterModalOpened, setIsCampaignStatusFilterModalOpened] = useState(false);
  const [filterText, setFilterText] = useState('');
  const history = useHistory();
  const debounceFilterText = useDebounce(filterText, 500);

  const filterByMany = useCallback(() => {
    dispatch(toggleTableLoaderVisibility({ isTableLoaderVisible: true }));
    getWorkOrdersWithFilter(archiveFilterStatus, campaignStatusFilter, debounceFilterText)
      .then((filteredWorkOrders: WorkOrder[]) => {
        dispatch(setWorkOrders({ workOrders: filteredWorkOrders }));
        dispatch(setSelectedWorkOrders({ selectedWorkOrders: [] }));
      })
      .catch(error => dispatch(
        setAlert({
          open: true,
          variant: 'danger',
          text: `${error.message}${cookieBanner}`
        })
      ))
      .finally(() => {
        dispatch(toggleTableLoaderVisibility({ isTableLoaderVisible: false }));
      });
  }, [campaignStatusFilter, archiveFilterStatus, debounceFilterText, dispatch]);

  useEffect(() => {
    filterByMany();
  }, [
    filterByMany,
    campaignStatusFilter,
    archiveFilterStatus,
    debounceFilterText
  ]);

  const onFilterTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value);
  };

  const getStatusSelectLabel = (options: CampaignStatus[]) => (
    options.length <= 1
      ? `Campaign Status: ${options}`
      : `Campaign Status: ${options[0]} + ${options.length - 1}`
  );

  return {
    onFilterTextChange,
    filterText,
    history,
    isCampaignStatusFilterModalOpened,
    setIsCampaignStatusFilterModalOpened,
    campaignStatusFilter,
    setCampaignStatusFilter,
    getStatusSelectLabel,
    archiveFilterStatus,
    isArchiveFilterModalOpened
  };
};

export default useBillingDashboardLogic;
